
import { computed, defineComponent, onMounted, ref } from "vue";
import FundingRoundStage from "@/components/wizard/steps/FundingRoundStage.vue";
import FundingRequirements from "@/components/wizard/steps/FundingRequirements.vue";
import RoundTimeline from "@/components/wizard/steps/RoundTimeline.vue";
import { StepperComponent } from "@/assets/ts/components";
import * as Yup from "yup";
import { useForm } from "vee-validate";
import { setCurrentPageBreadcrumbs } from "@/core/helpers/breadcrumb";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { useRoute } from "vue-router";
import { Actions } from "@/store/enums/StoreEnums";

interface IStep1 {
  name: string;
  jobTitle: string;
  telephone: string;
  linkedInProfileUrl: string;
}

interface IStep2 {
  introduction: string;
  bio: string;
}

interface IStep3 {
  linkToExternalDocuments: string;
  documents: unknown;
}

interface IStep4 {
  expertIndustrySectors: Array<number | string>;
  expertExpertise: Array<number | string>;
}

interface CreateAccount extends IStep1, IStep2, IStep3, IStep4 {}

export default defineComponent({
  name: "Investment-Opportunity",
  components: {
    FundingRoundStage,
    FundingRequirements,
    RoundTimeline,
  },
  setup() {
    const loading = ref<boolean>(false);
    const router = useRouter();
    const route = useRoute();
    const store = useStore();
    const formDataTemp = ref<FormData>(new FormData());
    const _stepperObj = ref<StepperComponent | null>(null);
    const verticalWizardRef = ref<HTMLElement | null>(null);
    const currentStepIndex = ref(0);
    const formData = ref<CreateAccount>({
      name: "",
      jobTitle: "",
      telephone: "",
      linkedInProfileUrl: "",
      introduction: "",
      bio: "",
      linkToExternalDocuments: "",
      documents: null,
      expertIndustrySectors: [],
      expertExpertise: [],
    });

    onMounted(() => {
      _stepperObj.value = StepperComponent.createInsance(
        verticalWizardRef.value as HTMLElement
      );

      setCurrentPageBreadcrumbs("Create Funding Round", [
        "Businesses",
        "Business Details",
      ]);
    });
    const createAccountSchema = [
      Yup.object({
        fundingRoundName: Yup.string().required().label("Name"),
      }),
      Yup.object({
        fundingRoundName: Yup.string().required().label("Name"),
        fundingRoundInvestmentRequired: Yup.string()
          .required()
          .label("Investment Required"),
        fundingRoundPreMoneyValuation: Yup.string()
          .required()
          .label("Pre-Money Valuation"),
        fundingRoundMinimumInvestment: Yup.string()
          .required()
          .label("Minimum Investment"),
      }),
      {},
    ];

    const currentSchema = computed(() => {
      return createAccountSchema[currentStepIndex.value];
    });

    const { resetForm, handleSubmit } = useForm<
      IStep1 | IStep2 | IStep3 | IStep4
    >({
      validationSchema: currentSchema,
    });

    const totalSteps = computed(() => {
      if (!_stepperObj.value) {
        return;
      }

      return _stepperObj.value.totatStepsNumber;
    });

    resetForm({
      values: {
        ...formData.value,
      },
    });

    const handleStep = handleSubmit((values) => {
      console.log({ values });
      formData.value = {
        ...formData.value,
        ...values,
      };

      currentStepIndex.value++;

      if (!_stepperObj.value) {
        return;
      }

      _stepperObj.value.goNext();
    });

    const previousStep = () => {
      if (!_stepperObj.value) {
        return;
      }

      currentStepIndex.value--;

      _stepperObj.value.goPrev();
    };

    const formSubmit = async () => {
      loading.value = true;
      try {
        formDataTemp.value.append(
          "founderBusinessId",
          route.query.businessId as string
        );
        const response = await store.dispatch(
          Actions.CREATE_FUNDING_ROUND,
          formDataTemp.value
        );
        if (response !== true) throw new Error();
        loading.value = false;
        store.commit("setAlert", {
          message: "Success",
          subMessage: `Funding Round ${"Created"}`,
          variant: "primary",
          duration: 4000,
          show: true,
        });
        setTimeout(() => {
          router.push(`/business/${route.query.businessId}`);
        }, 2000);
      } catch (err) {
        loading.value = false;
        store.commit("setAlert", {
          message: "Error",
          subMessage: "`Funding Round Creation Unsuccessful`",
          variant: "danger",
          duration: 4000,
          show: true,
        });
      }
    };

    return {
      loading,
      formDataTemp,
      verticalWizardRef,
      previousStep,
      handleStep,
      formSubmit,
      totalSteps,
      currentStepIndex,
    };
  },
});
